import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";

/**
 * Resets scroll position on route change and renders the floating
 * back-to-top control.
 *
 * The button is pinned to the LEFT edge on purpose: the LeadConnector chat
 * bubble occupies the bottom-right corner, so the left side is the only
 * position that never overlaps a widget, form or footer element.
 *
 * It stays mounted and transitions between states in CSS rather than being
 * added and removed by framer-motion. This component is mounted on every
 * route, so importing the animation library here alone was enough to keep
 * 126KB of JavaScript in the critical path of every first paint. `visibility`
 * takes the hidden button out of the tab order once it has faded out.
 */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Scroll back to top"
      aria-hidden={!visible}
      tabIndex={visible ? 0 : -1}
      className={`scroll-top-btn fixed bottom-6 left-4 sm:bottom-8 sm:left-6 z-[70] h-12 w-12 rounded-full flex items-center justify-center text-primary-foreground border border-white/20 backdrop-blur ${
        visible ? "scroll-top-btn--shown" : ""
      }`}
      style={{
        background: "linear-gradient(150deg, hsl(var(--primary)) 0%, hsl(var(--primary-dark)) 100%)",
        boxShadow: "0 10px 28px -8px hsl(var(--primary) / 0.55), 0 2px 6px hsl(100 26% 6% / 0.25)",
      }}
    >
      <ArrowUp className="h-5 w-5" strokeWidth={2.5} />
    </button>
  );
};

export default ScrollToTop;
